import { useEffect, useState } from "react";
import type { User } from "firebase/auth";
import { isSelfHosted } from "../config/infra";
import {
  getClaims,
  PLATFORM_PANEL_CLAIM,
  PLATFORM_PANEL_ROLE,
  PLATFORM_ROLES_CLAIM,
  subscribeToAuthChanges,
} from "../api/auth";

export type AuthStatus =
  | "loading"
  | "signed-out"
  | "authorized"
  | "unauthorized"
  | "error";

export interface PlatformAuthState {
  status: AuthStatus;
  user?: User;
  error?: string;
}

const usePlatformAuth = () => {
  const [state, setState] = useState<PlatformAuthState>({ status: "loading" });

  useEffect(() => {
    const unsubscribe = subscribeToAuthChanges(async (currentUser) => {
      if (!currentUser) {
        setState({ status: "signed-out" });
        return;
      }

      setState({ status: "loading" });
      try {
        const token = await getClaims(currentUser);
        const hasPanelAccess =
          token.claims[PLATFORM_PANEL_CLAIM] === true ||
          (isSelfHosted &&
            Array.isArray(token.claims[PLATFORM_ROLES_CLAIM]) &&
            token.claims[PLATFORM_ROLES_CLAIM]?.includes(PLATFORM_PANEL_ROLE));
        const rolesClaim = token.claims[PLATFORM_ROLES_CLAIM];
        const roles = Array.isArray(rolesClaim)
          ? rolesClaim.filter(
              (role): role is string => typeof role === "string"
            )
          : [];
        const hasGodRole = roles?.includes(PLATFORM_PANEL_ROLE);

        if (hasPanelAccess && hasGodRole) {
          setState({ status: "authorized", user: currentUser });
        } else {
          setState({ status: "unauthorized", user: currentUser });
        }
      } catch (error) {
        setState({
          status: "error",
          user: currentUser,
          error:
            error instanceof Error
              ? error.message
              : "Unable to verify access permissions.",
        });
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  return state;
};

export default usePlatformAuth;
